// AGC: Standard Preventive Maintenance Intervals by Machine Type
// AGC: Based on manufacturer recommendations for common farm equipment

import type { MachineCategory } from './machine'

/**
 * AGC: Maintenance interval definition
 */
export interface MaintenanceInterval {
  id: string
  hours: number                    // AGC: Interval in hours (0 = daily/per use)
  label: string                    // AGC: Display label (e.g., "Cada 250 horas")
  tasks: string[]                  // AGC: List of maintenance tasks
  isActiveByDefault: boolean       // AGC: Whether this interval is active by default
  priority: 'low' | 'medium' | 'high' | 'critical'
}

/**
 * AGC: Machine maintenance standards
 */
export interface MachineMaintenanceStandard {
  category: MachineCategory
  icon: string
  name: string
  intervals: MaintenanceInterval[]
}

/**
 * AGC: Tractor maintenance intervals
 * AGC: Most brands (John Deere, Case IH, New Holland, Massey Ferguson) follow this pattern
 */
export const TRACTOR_MAINTENANCE: MaintenanceInterval[] = [
  {
    id: 'trator-10h',
    hours: 10,
    label: 'Cada 10 horas / Diário',
    tasks: [
      'Verificar nível de óleo do motor',
      'Verificar nível de refrigerante',
      'Verificar nível de óleo hidráulico',
      'Engraxar pontos críticos',
      'Inspeção visual (vazamentos, correias, pneus)',
    ],
    isActiveByDefault: false, // AGC: User must activate daily checks
    priority: 'low',
  },
  {
    id: 'trator-50h',
    hours: 50,
    label: 'Cada 50 horas',
    tasks: [
      'Engraxar todos os bicos de graxa',
      'Verificar bateria',
      'Verificar entrada de ar',
      'Verificar correias',
    ],
    isActiveByDefault: true,
    priority: 'low',
  },
  {
    id: 'trator-100h',
    hours: 100,
    label: 'Cada 100 horas',
    tasks: [
      'Trocar óleo do motor (alguns modelos: 250h)',
      'Substituir ou limpar filtro de óleo',
    ],
    isActiveByDefault: true,
    priority: 'medium',
  },
  {
    id: 'trator-250h',
    hours: 250,
    label: 'Cada 250 horas ⭐',
    tasks: [
      'Trocar óleo do motor e filtro',
      'Limpar ou substituir filtro de ar',
      'Verificar sistema de combustível',
    ],
    isActiveByDefault: true,
    priority: 'high',
  },
  {
    id: 'trator-500h',
    hours: 500,
    label: 'Cada 500 horas',
    tasks: [
      'Trocar filtros de combustível',
      'Verificar filtros de transmissão e hidráulico',
      'Inspecionar embreagem, freios, direção',
    ],
    isActiveByDefault: true,
    priority: 'high',
  },
  {
    id: 'trator-1000h',
    hours: 1000,
    label: 'Cada 1.000 horas',
    tasks: [
      'Trocar óleo hidráulico',
      'Trocar óleo da transmissão',
      'Inspeção geral completa',
    ],
    isActiveByDefault: true,
    priority: 'critical',
  },
]

/**
 * AGC: Harvester/Combine maintenance intervals
 * AGC: Harvesters work hard, so maintenance is more frequent
 */
export const HARVESTER_MAINTENANCE: MaintenanceInterval[] = [
  {
    id: 'colheitadeira-10h',
    hours: 10,
    label: 'Cada 8-10 horas / Diário',
    tasks: [
      'Limpar máquina (MUITO importante)',
      'Engraxar todos os pontos',
      'Verificar correias, correntes, rolamentos',
      'Verificar óleo e refrigerante',
    ],
    isActiveByDefault: false, // AGC: User must activate daily checks
    priority: 'low',
  },
  {
    id: 'colheitadeira-50h',
    hours: 50,
    label: 'Cada 50-100 horas',
    tasks: [
      'Trocar óleo do motor e filtro',
      'Verificar componentes de trilha e corte',
    ],
    isActiveByDefault: true,
    priority: 'medium',
  },
  {
    id: 'colheitadeira-250h',
    hours: 250,
    label: 'Cada 250 horas',
    tasks: [
      'Trocar filtros de combustível',
      'Inspeção do sistema hidráulico',
    ],
    isActiveByDefault: true,
    priority: 'high',
  },
  {
    id: 'colheitadeira-season',
    hours: 500,
    label: 'Fim de safra',
    tasks: [
      'Serviço completo (rolamentos, correias, correntes, facas)',
      'Manutenção de armazenamento (óleo, prevenção de ferrugem)',
    ],
    isActiveByDefault: true,
    priority: 'critical',
  },
]

/**
 * AGC: Sprayer maintenance intervals
 * AGC: Self-propelled or tractor-mounted
 */
export const SPRAYER_MAINTENANCE: MaintenanceInterval[] = [
  {
    id: 'pulverizador-daily',
    hours: 10,
    label: 'Diário / Por uso',
    tasks: [
      'Limpar bicos e filtros',
      'Verificar óleo da bomba',
      'Verificar mangueiras e vazamentos',
    ],
    isActiveByDefault: false, // AGC: User must activate daily checks
    priority: 'low',
  },
  {
    id: 'pulverizador-50h',
    hours: 50,
    label: 'Cada 50-100 horas',
    tasks: [
      'Manutenção da bomba',
      'Verificar sistema de pressão',
      'Calibrar taxa de aplicação',
    ],
    isActiveByDefault: true,
    priority: 'medium',
  },
  {
    id: 'pulverizador-250h',
    hours: 250,
    label: 'Cada 250 horas',
    tasks: [
      'Substituir filtros',
      'Inspecionar válvulas e reguladores',
    ],
    isActiveByDefault: true,
    priority: 'high',
  },
  {
    id: 'pulverizador-season',
    hours: 500,
    label: 'Sazonal / Fim de safra',
    tasks: [
      'Lavagem completa do sistema',
      'Serviço de inverno/armazenamento',
    ],
    isActiveByDefault: true,
    priority: 'critical',
  },
]

/**
 * AGC: Planter maintenance intervals
 */
export const PLANTER_MAINTENANCE: MaintenanceInterval[] = [
  {
    id: 'plantadeira-daily',
    hours: 10,
    label: 'Diário / Por uso',
    tasks: [
      'Verificar discos de corte e sementes',
      'Limpar sistema de distribuição',
      'Verificar correntes e engrenagens',
    ],
    isActiveByDefault: false,
    priority: 'low',
  },
  {
    id: 'plantadeira-50h',
    hours: 50,
    label: 'Cada 50 horas',
    tasks: [
      'Engraxar todos os pontos',
      'Verificar tensão das correntes',
      'Calibrar distribuição de sementes',
    ],
    isActiveByDefault: true,
    priority: 'medium',
  },
  {
    id: 'plantadeira-season',
    hours: 250,
    label: 'Pré-plantio / Fim de safra',
    tasks: [
      'Revisão completa dos discos',
      'Substituir peças desgastadas',
      'Verificar sistema pneumático (se aplicável)',
      'Limpeza e armazenamento',
    ],
    isActiveByDefault: true,
    priority: 'critical',
  },
]

/**
 * AGC: Spreader/Distributor maintenance intervals
 * AGC: For fertilizer spreaders, lime spreaders, etc.
 */
export const SPREADER_MAINTENANCE: MaintenanceInterval[] = [
  {
    id: 'distribuidor-daily',
    hours: 10,
    label: 'Diário / Por uso',
    tasks: [
      'Limpar completamente (resíduos são corrosivos)',
      'Verificar discos e palhetas de distribuição',
      'Verificar esteira ou sistema de descarga',
    ],
    isActiveByDefault: false,
    priority: 'low',
  },
  {
    id: 'distribuidor-50h',
    hours: 50,
    label: 'Cada 50 horas',
    tasks: [
      'Engraxar todos os pontos',
      'Verificar correias e correntes',
      'Calibrar taxa de aplicação',
      'Inspecionar sistema de acionamento',
    ],
    isActiveByDefault: true,
    priority: 'medium',
  },
  {
    id: 'distribuidor-250h',
    hours: 250,
    label: 'Cada 250 horas',
    tasks: [
      'Verificar desgaste dos discos/palhetas',
      'Substituir peças desgastadas',
      'Verificar rolamentos',
      'Calibração completa',
    ],
    isActiveByDefault: true,
    priority: 'high',
  },
  {
    id: 'distribuidor-season',
    hours: 500,
    label: 'Fim de safra',
    tasks: [
      'Lavagem completa e secagem',
      'Aplicar óleo/graxa protetora',
      'Verificar pintura e aplicar anticorrosivo',
      'Armazenar em local coberto',
    ],
    isActiveByDefault: true,
    priority: 'critical',
  },
]

/**
 * AGC: Generic implement maintenance
 */
export const IMPLEMENT_MAINTENANCE: MaintenanceInterval[] = [
  {
    id: 'implemento-50h',
    hours: 50,
    label: 'Cada 50 horas',
    tasks: [
      'Engraxar pontos de articulação',
      'Verificar parafusos e porcas',
      'Inspecionar peças de desgaste',
    ],
    isActiveByDefault: true,
    priority: 'medium',
  },
  {
    id: 'implemento-250h',
    hours: 250,
    label: 'Cada 250 horas',
    tasks: [
      'Verificar estrutura e soldas',
      'Substituir peças desgastadas',
      'Pintura de proteção se necessário',
    ],
    isActiveByDefault: true,
    priority: 'high',
  },
]

/**
 * AGC: Get maintenance standards for a specific machine category
 */
export const getMaintenanceStandardsForCategory = (category: MachineCategory): MaintenanceInterval[] => {
  switch (category) {
    case 'trator':
      return TRACTOR_MAINTENANCE
    case 'colheitadeira':
      return HARVESTER_MAINTENANCE
    case 'pulverizador':
      return SPRAYER_MAINTENANCE
    case 'plantadeira':
      return PLANTER_MAINTENANCE
    case 'distribuidor':
      return SPREADER_MAINTENANCE
    case 'implemento':
      return IMPLEMENT_MAINTENANCE
    default:
      // AGC: Default to implement maintenance for other categories
      return IMPLEMENT_MAINTENANCE
  }
}

/**
 * AGC: Get category display name
 */
export const getCategoryDisplayName = (category: MachineCategory): string => {
  const names: Record<MachineCategory, string> = {
    trator: 'Trator',
    colheitadeira: 'Colheitadeira',
    plantadeira: 'Plantadeira',
    pulverizador: 'Pulverizador',
    distribuidor: 'Distribuidor',
    implemento: 'Implemento',
    irrigacao: 'Irrigação',
    transporte: 'Transporte',
    outros: 'Outros',
  }
  return names[category] || 'Equipamento'
}

/**
 * AGC: Get priority color for maintenance interval
 */
export const getIntervalPriorityColor = (priority: MaintenanceInterval['priority']): string => {
  switch (priority) {
    case 'low':
      return 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
    case 'medium':
      return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
    case 'high':
      return 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
    case 'critical':
      return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
    default:
      return 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
  }
}

/**
 * AGC: User's maintenance interval preferences (stored per machine)
 */
export interface MachineMaintenancePreferences {
  machineId: string
  activeIntervals: string[]  // AGC: Array of interval IDs that are active
  customIntervals?: MaintenanceInterval[]  // AGC: User-defined custom intervals
}
